import { FaSpinner } from "react-icons/fa";

const FeedbackModal = ({
  showFeedbackModal,
  setShowFeedbackModal,
  setShowPassengerForm,
  setShowPriceModal,
  setBookingSuccessData,
  rating,
  setRating,
  feedbackText,
  setFeedbackText,
  isSubmittingFeedback,
  handleFeedbackSubmit,
}) => {
  if (!showFeedbackModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Share Your Experience
            </h2>
            <p className="text-gray-600">
              How was your flight booking experience with us?
            </p>
          </div>

          <div className="space-y-6 mb-6">
            <StarRating rating={rating} setRating={setRating} />
            <FeedbackText feedbackText={feedbackText} setFeedbackText={setFeedbackText} />
          </div>

          <div className="flex gap-3">
            <SkipButton 
              setShowFeedbackModal={setShowFeedbackModal}
              setShowPassengerForm={setShowPassengerForm}
              setShowPriceModal={setShowPriceModal}
              setBookingSuccessData={setBookingSuccessData}
              setRating={setRating}
              setFeedbackText={setFeedbackText}
              isSubmittingFeedback={isSubmittingFeedback}
            />
            <SubmitButton 
              handleFeedbackSubmit={handleFeedbackSubmit}
              isSubmittingFeedback={isSubmittingFeedback}
              rating={rating}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StarRating = ({ rating, setRating }) => (
  <div className="text-center">
    <label className="block text-sm font-medium text-gray-700 mb-3">
      Rate your experience
    </label>
    <div className="flex justify-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <svg
            className={`w-10 h-10 ${
              star <= rating
                ? "text-yellow-400 fill-current"
                : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
    {rating > 0 && (
      <p className="text-sm text-gray-600 mt-2">
        {rating === 1 && "Poor"}
        {rating === 2 && "Fair"}
        {rating === 3 && "Good"}
        {rating === 4 && "Very Good"}
        {rating === 5 && "Excellent"}
      </p>
    )}
  </div>
);

const FeedbackText = ({ feedbackText, setFeedbackText }) => (
  <div>
    <label
      htmlFor="feedback-text"
      className="block text-sm font-medium text-gray-700 mb-2"
    >
      Tell us more about your experience (Optional)
    </label>
    <textarea
      id="feedback-text"
      rows={4}
      value={feedbackText}
      onChange={(e) => setFeedbackText(e.target.value)}
      placeholder="Share your thoughts, suggestions, or any feedback you'd like to provide..."
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
    />
  </div>
);

const SkipButton = ({
  setShowFeedbackModal,
  setShowPassengerForm,
  setShowPriceModal,
  setBookingSuccessData,
  setRating,
  setFeedbackText,
  isSubmittingFeedback,
}) => (
  <button
    onClick={() => {
      setShowFeedbackModal(false);
      setShowPassengerForm(false);
      setShowPriceModal(false);
      setBookingSuccessData(null);
      setRating(0);
      setFeedbackText("");
    }}
    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
    disabled={isSubmittingFeedback}
  >
    Skip
  </button>
);

const SubmitButton = ({ handleFeedbackSubmit, isSubmittingFeedback, rating }) => (
  <button
    onClick={handleFeedbackSubmit}
    disabled={isSubmittingFeedback || rating === 0}
    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {isSubmittingFeedback ? (
      <span className="flex items-center justify-center gap-2">
        <FaSpinner className="animate-spin" />
        Submitting...
      </span>
    ) : (
      "Submit Feedback"
    )}
  </button>
);

export default FeedbackModal;